import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft,
  Clock,
  CheckCircle2,
  PlayCircle,
  ClipboardList,
  Loader2
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAllLearningAssignments } from "@/hooks/useLearningAssignments";
import { AssignmentFilters, type FilterState } from "@/components/admin/assignments/AssignmentFilters";
import { AssignmentList } from "@/components/admin/assignments/AssignmentList";
import { CreateAssignmentDialog } from "@/components/admin/assignments/CreateAssignmentDialog";
import type { AssignmentStatus } from "@/types/assignments";

export default function AdminAssignments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const createDialogRef = useRef<HTMLButtonElement>(null);
  
  // Get initial status from URL params
  const initialStatus = searchParams.get("status") as AssignmentStatus | null;
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>(() => {
    if (initialStatus && ["not_started", "in_progress", "completed"].includes(initialStatus)) {
      return { status: initialStatus };
    }
    return {};
  });
  
  // Clear filters function
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Check if user is admin
  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ["is_admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch all assignments
  const { 
    data: assignments, 
    isLoading: assignmentsLoading, 
    error,
    refetch 
  } = useAllLearningAssignments();

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  // Redirect if not admin
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    } else if (!adminLoading && isAdmin === false) {
      navigate("/dashboard");
    }
  }, [authLoading, adminLoading, user, isAdmin, navigate]);

  // Note: early null return removed — handled inside DashboardLayout below

  // Apply filters - memoized for performance
  const filteredAssignments = useMemo(() => {
    if (!assignments) return [];
    
    return assignments.filter((assignment) => {
      // Type filter
      if (filters.type && assignment.type !== filters.type) {
        return false;
      }
      
      // User filter
      if (filters.userId && assignment.user_id !== filters.userId) {
        return false;
      }
      
      // Status filter
      if (filters.status && assignment.status !== filters.status) {
        return false;
      }
      
      // Search filter (user name or item name)
      if (filters.search) {
        const query = filters.search.toLowerCase();
        const matchesUser = assignment.user_name.toLowerCase().includes(query);
        const matchesItem = assignment.item_name.toLowerCase().includes(query);
        if (!matchesUser && !matchesItem) {
          return false;
        }
      }
      
      return true;
    });
  }, [assignments, filters]);

  // Stats (from unfiltered assignments) - memoized
  const stats = useMemo(() => {
    if (!assignments) return { total: 0, notStarted: 0, inProgress: 0, completed: 0 };
    
    return {
      total: assignments.length,
      notStarted: assignments.filter(a => a.status === 'not_started').length,
      inProgress: assignments.filter(a => a.status === 'in_progress').length,
      completed: assignments.filter(a => a.status === 'completed').length,
    };
  }, [assignments]);

  // Check if any filters are active
  const hasActiveFilters = Boolean(filters.type || filters.userId || filters.status || filters.search);

  const isLoading = authLoading || adminLoading || assignmentsLoading;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              className="shrink-0"
              aria-label="Go back to admin dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Learning Assignments</h1>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                Manage curriculum and course assignments for users
              </p>
            </div>
          </div>
          <CreateAssignmentDialog onSuccess={() => refetch()} />
        </div>

        {/* Stats */}
        <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-4">
          <Card className="transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Total</CardTitle>
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats.total}
              </div>
            </CardContent>
          </Card>
          <Card className="transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Not Started</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats.notStarted}
              </div>
            </CardContent>
          </Card>
          <Card className="transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">In Progress</CardTitle>
              <PlayCircle className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats.inProgress}
              </div>
            </CardContent>
          </Card>
          <Card className="transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Completed</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats.completed}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <AssignmentFilters 
              onFilterChange={handleFilterChange} 
              initialStatus={initialStatus || undefined}
            />
          </CardContent>
        </Card>

        {/* Results Count */}
        {!isLoading && assignments && assignments.length > 0 && (
          <div 
            className="flex items-center justify-between text-sm"
            role="status"
            aria-live="polite"
          >
            <p className="text-muted-foreground">
              {hasActiveFilters ? (
                <>
                  Showing <span className="font-medium text-foreground">{filteredAssignments.length}</span> of{" "}
                  <span className="font-medium text-foreground">{assignments.length}</span> assignments
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">{assignments.length}</span> total assignments
                </>
              )}
            </p>
          </div>
        )}

        {/* Assignments List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">
              Assignments
              {hasActiveFilters && !isLoading && ` (${filteredAssignments.length})`}
            </CardTitle>
            <CardDescription className="text-sm">
              All learning assignments across curricula and courses
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AssignmentList 
              assignments={filteredAssignments} 
              isLoading={isLoading}
              onRefresh={() => refetch()}
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
              error={error || null}
            />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
